import { prEvidenceQuery } from './prEvidence';
import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { epicPullRequests, pipelineLabel } from "./model";
import type { BoardItem } from "./model";

const states: Record<string, string> = { active: "Open", completed: "Merged", abandoned: "Abandoned", unavailable: "Status unavailable" };
function PullRequest({ link }: { link: ReturnType<typeof epicPullRequests>[number] }) {
  const query = useQuery(prEvidenceQuery(link));
  const data = query.data;
  return <article className="board-pr">
    <div className="board-milestone-top"><a href={link.url} target="_blank" rel="noreferrer"><strong>{link.label} · {data?.title || "Open in Azure DevOps"} ↗</strong></a>
      <span>{data ? (data.draft ? "Draft" : states[data.state] ?? data.state) : query.isPending ? "Loading…" : "Status unavailable"}</span></div>
    <div className="board-pr-tickets">{link.items.map((item) => <a key={item.id} href={item.url} target="_blank" rel="noreferrer">#{item.id} {item.title} ↗</a>)}</div>
    {query.isError && <p role="status" className="board-risk">Refresh failed. {data ? "Showing the previous snapshot below." : "Open the PR to check its status."}</p>}
    {data && <>
      {data.prError ? <p className="board-risk">{data.prError}</p> : <p className="board-note">{data.approvals} positive reviewer votes · {data.changesRequested} waiting/reject votes. Branch policies determine merge readiness.</p>}
      <h3>PR validation pipelines</h3>
      {data.pipelineError && <p className="board-risk">{data.pipelineError}</p>}
      {!data.pipelineError && !data.pipelines.length && <p className="board-note">No retained PR validation runs found. This does not mean checks passed.</p>}
      <ul className="board-pipelines">{data.pipelines.map((pipeline) => <li key={pipeline.id}>
        <a href={pipeline.url} target="_blank" rel="noreferrer">{pipeline.name} ↗</a>
        <strong>{pipelineLabel(pipeline.status, pipeline.result)}</strong>
        <span className="board-note">{pipeline.currentRevision === true ? "Current merge revision" : pipeline.currentRevision === false ? "Earlier merge revision" : "Revision not verified"}</span>
      </li>)}</ul>
      <p className="board-note">Checked {new Date(data.fetchedAt).toLocaleString("en-GB")}</p>
    </>}
  </article>;
}
export function PullRequests({ items }: { items: BoardItem[] }) {
  const links = epicPullRequests(items);
  const [limit, setLimit] = useState(10);
  return <section className="board-prs" aria-label="Epic pull requests and pipelines">
    <h2>PRs & pipelines</h2>
    <p className="board-note">PRs linked to this epic and its descendants, newest numbers first. Latest retained validation run per pipeline; CI success does not prove deployment. Work filters do not hide this evidence.</p>
    {!links.length && <p className="board-empty">No linked PRs. Link a PR to this epic or a work item beneath it in Azure DevOps.</p>}
    {links.slice(0, limit).map((link) => <PullRequest key={link.url} link={link} />)}
    {links.length > limit && <button className="board-button" onClick={() => { setLimit(limit + 10); }}>Show 10 more ({links.length - limit} remaining)</button>}
  </section>;
}
